import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Mail } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import Footer from "@/components/ledger/Footer";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";

/**
 * FounderPage — dedicated brand-keyword page for "Darshit Anadkat".
 *
 * Built to rank for: "darshit anadkat", "darshit anadkat vilora",
 * "darshit anadkat founder", "founder of vilora impex", "vilora founder".
 *
 * Full Person JSON-LD with sameAs to external profiles, jobTitle, worksFor,
 * knowsAbout, and an Article schema that frames the page as a profile.
 */

const SITE = "https://www.viloraimpex.com";
const PAGE_URL = `${SITE}/founder/darshit-anadkat`;
const ORG_REF = `${SITE}/#organization`;

const SAME_AS = [
  "https://www.darshitanadkat.com",
  "https://www.linkedin.com/in/darshitanadkat",
  "https://x.com/darshitanadkat",
  "https://www.instagram.com/darshit.anadkat",
];

const KNOWS_ABOUT = [
  "International trade",
  "Indian rice export",
  "Basmati rice supply",
  "Spice export from India",
  "Mango export from India",
  "Agricultural export logistics",
  "FSSAI compliance",
  "APEDA registration",
  "Phytosanitary certification",
  "Export documentation",
  "Bill of Lading",
  "Letter of Credit",
  "FOB Mundra",
  "FOB Kandla",
  "Cross-border trade Gujarat",
  "Indian trading house",
  "Founder-led B2B export",
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Person",
      "@id": `${PAGE_URL}#person`,
      "name": "Darshit Anadkat",
      "givenName": "Darshit",
      "familyName": "Anadkat",
      "alternateName": ["D. Anadkat", "Darshit"],
      "url": PAGE_URL,
      "image": `${SITE}/logo.png`,
      "jobTitle": "Founder",
      "worksFor": { "@id": ORG_REF },
      "nationality": { "@type": "Country", "name": "India" },
      "homeLocation": { "@type": "Place", "name": "Rajkot, Gujarat, India" },
      "knowsAbout": KNOWS_ABOUT,
      "knowsLanguage": ["English", "Hindi", "Gujarati"],
      "sameAs": SAME_AS,
      "description":
        "Founder of Vilora Impex — an Indian trading house exporting rice, basmati, spices, dry fruit and fresh produce from Gujarat to 12+ destinations across the Gulf, Middle East, East Africa and Southeast Asia.",
    },
    {
      "@type": "ProfilePage",
      "@id": `${PAGE_URL}#webpage`,
      "url": PAGE_URL,
      "name": "Darshit Anadkat — Founder of Vilora Impex",
      "isPartOf": { "@id": `${SITE}/#website` },
      "about": { "@id": `${PAGE_URL}#person` },
      "mainEntity": { "@id": `${PAGE_URL}#person` },
      "inLanguage": "en",
      "datePublished": "2026-05-20",
      "dateModified": "2026-05-20",
      "breadcrumb": { "@id": `${PAGE_URL}#breadcrumb` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${PAGE_URL}#breadcrumb`,
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
        { "@type": "ListItem", "position": 2, "name": "Founder", "item": `${SITE}/founder` },
        { "@type": "ListItem", "position": 3, "name": "Darshit Anadkat", "item": PAGE_URL },
      ],
    },
    {
      "@type": "Article",
      "@id": `${PAGE_URL}#article`,
      "headline": "Darshit Anadkat — Founder of Vilora Impex",
      "alternativeHeadline": "From a Rajkot counter to a Mundra container",
      "description":
        "Darshit Anadkat is the founder of Vilora Impex, an Indian trading house headquartered in Rajkot, Gujarat. Profile, principles, ledger and how to reach the export desk directly.",
      "image": `${SITE}/og.jpg`,
      "datePublished": "2026-05-20",
      "dateModified": "2026-05-20",
      "author": { "@id": `${PAGE_URL}#person` },
      "publisher": { "@id": ORG_REF },
      "mainEntityOfPage": PAGE_URL,
      "inLanguage": "en",
      "about": { "@id": `${PAGE_URL}#person` },
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".founder-hero-title", ".founder-hero-deck", ".founder-section-lead"],
      },
    },
  ],
};

export default function FounderPage() {
  useSeo({
    title:
      "Darshit Anadkat — Founder of Vilora Impex | Indian Trading House",
    description:
      "Darshit Anadkat is the founder of Vilora Impex, an Indian trading house exporting premium rice, basmati, spices, dry fruit and mango from Gujarat. Profile, principles and direct contact.",
    path: "/founder/darshit-anadkat",
    image: `${SITE}/og.jpg`,
    imageAlt: "Darshit Anadkat — Founder of Vilora Impex, Indian Trading House",
    ogType: "profile",
    jsonLd: JSON_LD,
  });

  return (
    <main className="ledger founder-page relative min-h-screen" data-testid="founder-page">
      <FounderStyles />
      <ScrollProgress />
      <Navbar />

      <section className="founder-hero paper" id="founder-top">
        <div className="founder-shell">
          <nav className="founder-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">·</span>
            <span aria-current="page">Darshit Anadkat</span>
          </nav>

          <div className="founder-folio">
            <span className="folio">N&deg; 21</span>
            <span className="rule rule-grow" />
            <span className="folio" style={{ color: "var(--ink-mute)" }}>FOUNDER</span>
          </div>

          <span className="brass-ribbon founder-eyebrow">A founder-led Indian trading house</span>

          <h1 className="display founder-hero-title">Darshit Anadkat</h1>
          <p className="founder-hero-deck">
            Founder of <strong>Vilora Impex</strong> — an Indian trading house headquartered in
            Rajkot, Gujarat. Custodian of the family ledger that began across the counter at
            Dariyalal Sales Agency and now ships premium rice, basmati, spices, dry fruit and
            mango out of Mundra and Kandla to twelve destination ports.
          </p>

          <div className="founder-hero-meta">
            <span><strong>Role</strong> Founder</span>
            <span><strong>Based</strong> Rajkot, Gujarat, India</span>
            <span><strong>Building</strong> Vilora Impex &middot; since 2025</span>
          </div>

          <div className="founder-actions">
            <a href="mailto:exports@viloraimpex.com" className="btn-primary">
              <span className="btn-code">DESK</span>
              <span>Write to the founder</span>
              <span className="btn-arrow"><Mail size={14} strokeWidth={1.6} /></span>
            </a>
            <a
              href="https://www.darshitanadkat.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
            >
              <span>Personal site</span>
              <span className="btn-arrow"><ArrowUpRight size={14} strokeWidth={1.6} /></span>
            </a>
          </div>
        </div>
      </section>

      <section className="founder-section paper">
        <div className="founder-shell">
          <h2 className="display founder-h2">A short profile of Darshit Anadkat</h2>
          <p className="founder-section-lead">
            Darshit Anadkat is the founder of <strong>Vilora Impex</strong>, the
            international export arm of a Gujarat-based family business that runs
            <em> Dariyalal Sales Agency</em> (retail), <em>Vilora Organic Essential Pvt. Ltd.</em>
            (the milling and processing house), and Vilora Impex (the export desk). He
            leads the export ledger end-to-end — sourcing, grading, documentation,
            shipping and buyer relationships — and signs the documentation packet on
            every container that sails.
          </p>
          <p className="founder-body">
            The work is unromantically simple: source clean lots from milling partners
            in Punjab, Andhra Pradesh and Gujarat; hand-grade and sortex-clean to buyer
            specification; load at Mundra or Kandla; ship with phytosanitary, FSSAI,
            APEDA, certificate-of-origin and Bill of Lading paperwork couriered within
            48 hours of sail; track the container live to port of arrival; stay on the
            line until the buyer signs off. Repeat. The ledger is what compounds.
          </p>
        </div>
      </section>

      <section className="founder-section founder-section--tint paper">
        <div className="founder-shell">
          <h2 className="display founder-h2">What Vilora Impex does, in one paragraph</h2>
          <p className="founder-body">
            Vilora Impex is a registered Indian trading house. We export
            <strong> premium basmati rice (1121, 1509, Pusa)</strong>, non-basmati rice
            (Sona Masuri, IR64, idli), sortex-cleaned varieties, single-origin spices
            (turmeric, cumin, coriander, chilli), Indian dry fruits, pulses, fresh
            produce and mango (Kesar, Alphonso) from Gujarat to twelve destination ports
            across the Gulf, Middle East, East Africa, the Maldives and Southeast Asia.
            We hold FSSAI, APEDA, Spices Board of India and ISO 22000 credentials.
            Founder-led, family-built, ledger-disciplined.
          </p>
          <ul className="founder-list">
            <li><Link to="/rice-exporter-from-india">Rice Exporter from India</Link></li>
            <li><Link to="/basmati-rice-exporter-from-india">Basmati Rice Exporter from India</Link></li>
            <li><Link to="/1121-basmati-rice-exporter">1121 Basmati Rice Exporter</Link></li>
            <li><Link to="/1509-basmati-rice-exporter">1509 Basmati Rice Exporter</Link></li>
            <li><Link to="/premium-rice-supplier-india">Premium Rice Supplier from India</Link></li>
            <li><Link to="/sortex-cleaned-rice-exporter">Sortex-Cleaned Rice Exporter</Link></li>
            <li><Link to="/wholesale-rice-supplier-from-india">Wholesale Rice Supplier from India</Link></li>
            <li><Link to="/spices-exporter-from-india">Spices Exporter from India</Link></li>
            <li><Link to="/mango-exporter-from-india">Mango Exporter from India</Link></li>
            <li><Link to="/dry-fruits-exporter-from-india">Dry Fruits Exporter from India</Link></li>
          </ul>
        </div>
      </section>

      <section className="founder-section paper">
        <div className="founder-shell">
          <h2 className="display founder-h2">The principles I run the ledger by</h2>
          <ol className="founder-principles">
            <li>
              <strong>Grade by sight, weigh twice.</strong> The discipline learned across
              the original retail counter at Dariyalal Sales Agency is the same one applied
              at the milling floor today. No lot leaves without being hand-checked.
            </li>
            <li>
              <strong>The ledger is the answer.</strong> Every container ships with a paper
              trail — sortex pass log, weighbridge slips, photographic grade proofs,
              independent lab COA, and the full documentation packet — so the buyer never
              has to ask "what did we get?".
            </li>
            <li>
              <strong>Founder signature on every container.</strong> The documentation packet
              is signed by the founder. There is one accountable person at the export desk,
              not a chain of brokers.
            </li>
            <li>
              <strong>Buyer trust compounds; cheap shortcuts don't.</strong> We turn down
              lots that don't meet spec rather than over-promise. Repeat buyers are the
              entire business.
            </li>
            <li>
              <strong>Documentation within 48 hours of sail.</strong> Phytosanitary, FSSAI,
              APEDA, certificate of origin, Bill of Lading and lab COA reach the buyer
              before the container does.
            </li>
            <li>
              <strong>Live tracking until sign-off.</strong> The relationship doesn't end at
              load-out. We stay on the line until the buyer confirms arrival at destination
              port.
            </li>
          </ol>
        </div>
      </section>

      <section className="founder-section founder-section--tint paper">
        <div className="founder-shell">
          <h2 className="display founder-h2">Background and the family ledger</h2>
          <p className="founder-body">
            Darshit grew up in Rajkot, Gujarat, in a family that has been weighing grain
            across a counter for decades. The business began as <strong>Dariyalal Sales
            Agency</strong> — a retail shop that still operates today — moved into wholesale
            supply across Gujarat, and incorporated <strong>Vilora Organic Essential Pvt.
            Ltd.</strong> as the manufacturing and dry-fruit processing arm. Vilora Impex,
            the export desk, is the fourth chapter — designed to take that same ledger
            discipline to international buyers without the friction of multi-layered broker
            chains.
          </p>
          <p className="founder-body">
            The full four-chapter history of the family business is documented on{" "}
            <Link to="/our-story" className="footer-link">our story page</Link>. The
            personal essays and writing live on{" "}
            <a
              href="https://www.darshitanadkat.com"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              darshitanadkat.com
            </a>.
          </p>
        </div>
      </section>

      <section className="founder-section paper">
        <div className="founder-shell">
          <h2 className="display founder-h2">Where to reach Darshit Anadkat</h2>
          <ul className="founder-contact">
            <li>
              <span className="founder-contact-label">Export desk email</span>
              <a href="mailto:exports@viloraimpex.com">exports@viloraimpex.com</a>
            </li>
            <li>
              <span className="founder-contact-label">Phone &middot; WhatsApp</span>
              <a href="tel:+918669999901">+91 86699 99901</a>
            </li>
            <li>
              <span className="founder-contact-label">Personal site</span>
              <a href="https://www.darshitanadkat.com" target="_blank" rel="noopener noreferrer">
                darshitanadkat.com
              </a>
            </li>
            <li>
              <span className="founder-contact-label">LinkedIn</span>
              <a href="https://www.linkedin.com/in/darshitanadkat" target="_blank" rel="noopener noreferrer">
                linkedin.com/in/darshitanadkat
              </a>
            </li>
            <li>
              <span className="founder-contact-label">Based in</span>
              <span>Rajkot, Gujarat, India</span>
            </li>
          </ul>
          <div className="founder-actions">
            <Link to="/" className="btn-ghost">
              <span><ArrowLeft size={14} strokeWidth={1.6} /></span>
              <span>Back to Vilora Impex</span>
            </Link>
            <a href="mailto:exports@viloraimpex.com" className="btn-primary">
              <span className="btn-code">EXPORT</span>
              <span>Open a conversation</span>
              <span className="btn-arrow">→</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}

function FounderStyles() {
  return (
    <style>{`
      .founder-page { color: var(--ink); }
      .founder-shell {
        max-width: 880px;
        margin: 0 auto;
        padding: 0 clamp(20px, 4vw, 40px);
      }
      .founder-hero {
        padding: clamp(48px, 7vw, 88px) 0 clamp(32px, 5vw, 56px);
        position: relative;
      }
      .founder-breadcrumb {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--ink-mute);
        display: flex;
        gap: 10px;
        align-items: center;
        margin-bottom: 18px;
      }
      .founder-breadcrumb a { color: var(--brass-deep); text-decoration: none; }
      .founder-folio {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
      }
      .founder-eyebrow { display: inline-block; margin-bottom: 18px; }
      .founder-hero-title {
        font-size: clamp(40px, 6.4vw, 76px);
        line-height: 0.98;
        margin: 0 0 18px;
        letter-spacing: -0.005em;
      }
      .founder-hero-deck {
        font-family: 'Crimson Pro', 'Tiempos Text', Georgia, serif;
        font-size: clamp(18px, 1.8vw, 22px);
        line-height: 1.55;
        color: var(--ink-soft);
        max-width: 720px;
        margin-bottom: 22px;
      }
      .founder-hero-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 18px 28px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 12px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ink-mute);
        margin-bottom: 28px;
      }
      .founder-hero-meta strong {
        color: var(--brass-deep);
        font-weight: 500;
        margin-right: 8px;
      }
      .founder-actions {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
      }

      .founder-section {
        padding: clamp(36px, 5vw, 64px) 0;
      }
      .founder-section--tint {
        background: var(--paper-2, #ECE3CE);
        background: linear-gradient(180deg, rgba(168,132,61,0.04), rgba(168,132,61,0)), var(--paper-1, #FAF6EA);
      }
      .founder-h2 {
        font-size: clamp(26px, 3.2vw, 38px);
        line-height: 1.1;
        margin-bottom: 18px;
        letter-spacing: -0.003em;
      }
      .founder-section-lead,
      .founder-body {
        font-family: 'Crimson Pro', 'Tiempos Text', Georgia, serif;
        font-size: clamp(16px, 1.4vw, 18px);
        line-height: 1.7;
        color: var(--ink-soft);
        margin-bottom: 16px;
      }
      .founder-list {
        list-style: none;
        padding: 0;
        margin: 16px 0 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 8px 22px;
      }
      .founder-list a {
        color: var(--brass-deep);
        text-decoration: none;
        font-family: 'JetBrains Mono', monospace;
        font-size: 12px;
        letter-spacing: 0.10em;
        text-transform: uppercase;
        border-bottom: 1px dotted rgba(168,132,61,0.4);
        padding-bottom: 2px;
        display: inline-block;
      }
      .founder-list a:hover { color: var(--brass); border-bottom-color: var(--brass); }

      .founder-principles {
        list-style: none;
        counter-reset: principle;
        padding: 0;
        margin: 20px 0 0;
        display: grid;
        gap: 18px;
      }
      .founder-principles li {
        counter-increment: principle;
        padding-left: 50px;
        position: relative;
        font-family: 'Crimson Pro', serif;
        font-size: 17px;
        line-height: 1.6;
        color: var(--ink-soft);
      }
      .founder-principles li::before {
        content: counter(principle, decimal-leading-zero);
        position: absolute;
        left: 0;
        top: 1px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 14px;
        color: var(--brass);
        letter-spacing: 0.05em;
      }
      .founder-principles strong { color: var(--ink); font-weight: 600; }

      .founder-contact {
        list-style: none;
        padding: 0;
        margin: 0 0 24px;
        display: grid;
        gap: 14px;
      }
      .founder-contact li {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid rgba(168,132,61,0.20);
      }
      .founder-contact-label {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--brass-deep);
        min-width: 160px;
      }
      .founder-contact a {
        color: var(--ink);
        text-decoration: none;
        border-bottom: 1px solid rgba(168,132,61,0.45);
        padding-bottom: 1px;
      }
      .founder-contact a:hover { color: var(--brass-deep); border-bottom-color: var(--brass); }
    `}</style>
  );
}
